"use client";

import { useState, useRef, useCallback } from "react";
import {
  ArrowLeft,
  Upload,
  Trash2,
  Eye,
  Info,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import { Avatar, Button } from "antd";
import moment from "moment";
import { CiHeart } from "react-icons/ci";

// File size limits (in bytes)
const FILE_SIZE_LIMITS = {
  poster: 2 * 1024 * 1024, // 2MB
  agenda: 10 * 1024 * 1024, // 10MB
};

// Allowed file types
const ALLOWED_FILE_TYPES = {
  poster: ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"],
  agenda: [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ],
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return (
    Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  );
};

const validateFile = (file, type) => {
  const errors = [];

  // Check file size
  if (file.size > FILE_SIZE_LIMITS[type]) {
    const maxSize = formatFileSize(FILE_SIZE_LIMITS[type]);
    const currentSize = formatFileSize(file.size);
    errors.push(
      `File size (${currentSize}) exceeds the maximum limit of ${maxSize}`
    );
  }

  // Check file type
  if (!ALLOWED_FILE_TYPES[type].includes(file.type)) {
    const allowedTypes =
      type === "poster" ? "JPEG, PNG, GIF, WebP" : "PDF, DOC, DOCX";
    errors.push(`Invalid file type. Allowed types: ${allowedTypes}`);
  }

  return errors;
};

const MobilePreview = ({ formData, selectedImage }) => (
  <div className="bg-black rounded-3xl w-full max-w-lg mx-auto">
    <div className="bg-white rounded-3xl overflow-hidden">
      {/* Phone Header */}
      <div className="bg-gray-50 px-4 py-2 flex justify-between items-center">
        <div className="flex items-center space-x-1">
          <div className="w-1 h-1 bg-black rounded-full"></div>
          <div className="w-1 h-1 bg-black rounded-full"></div>
          <div className="w-1 h-1 bg-black rounded-full"></div>
        </div>
        <div className="text-xs font-medium"></div>
        <div className="flex items-center space-x-1">
          <div className="w-4 h-2 border border-black rounded-sm">
            <div className="w-3 h-1 bg-black rounded-sm m-0.5"></div>
          </div>
        </div>
      </div>

      {/* App Header */}
      <div className="bg-white px-4 py-3 flex items-center justify-between border-b">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
            U
          </div>
          <span className="text-sm">Location</span>
        </div>
        <div className="flex items-center space-x-3">
          <Eye className="w-4 h-4 text-gray-400" />
          <div className="relative">
            <div className="w-6 h-6 bg-gray-200 rounded-full"></div>
            <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full text-xs text-white flex items-center justify-center">
              1
            </div>
          </div>
        </div>
      </div>

      {/* Event Content */}
      <div className="p-4">
        <span className="text-xs text-gray-500 block mb-2">Your Event</span>

        <div className="mx-auto w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          {/* Header */}
          <div className="relative">
            {/* Hero image */}
            <img
              src={
                selectedImage
                  ? selectedImage
                  : "https://www.trybooking.com/media/gbqj0sea/wellness_header.webp"
              }
              alt="Mental wellness event hero"
              width={532}
              height={400}
              className="h-48 w-full object-cover"
            />

            {/* Orange 'Upcoming' badge */}
            <span className="absolute right-0 top-0 rounded-md bg-orange-500 px-3 py-1 text-xs font-medium text-white font-poppins">
              Upcoming
            </span>

            {/* Date block */}
            <div className="absolute -bottom-4 left-4 flex flex-col items-center justify-center rounded-lg bg-white px-3 py-2 text-gray-900 shadow">
              <span className="text-[16px] font-poppins leading-none">
                {" "}
                {formData?.date ? moment(formData?.date).format("d") : "10"}
              </span>
              <span className="text-[16px] font-poppins">
                {" "}
                {formData?.date
                  ? moment(formData?.date).format("MMM")
                  : "Apr"}
              </span>
            </div>

            {/* Favourite icon */}
            <div
              aria-label="Add to favourites"
              className="absolute bottom-4 right-4 flex h-9 w-9 items-center justify-center rounded-full bg-white text-gray-600 shadow"
            >
              <CiHeart width={40} />
            </div>
          </div>

          {/* Body */}
          <div className="space-y-4 px-6 py-4">
            {/* Title + ONLINE badge */}
            <div className="flex flex-wrap items-start justify-between gap-2">
              <h2 className="flex-1 text-lg font-semibold text-[#4B61FF]">
                {formData.eventTitle || "Mental Wellness for Students"}
              </h2>

              <span className="flex items-center gap-1 rounded-md bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 font-poppins">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3 w-3"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2a9.93 9.93 0 00-7.07 2.93A10 10 0 1019.07 4.93 9.93 9.93 0 0012 2zm-.1 18A8 8 0 114 12a8 8 0 017.9 8zm3.78-7h2.44A6.84 6.84 0 0018 12a7 7 0 00-.32-2h-2.56a10.89 10.89 0 010 4zm-7.36 0h4.96a8.9 8.9 0 000-4H8.32a8.9 8.9 0 000 4zM6 12a6.84 6.84 0 00.32 2h2.44a10.83 10.83 0 010-4H6.32A6.84 6.84 0 006 12z" />
                </svg>
                Online
              </span>
            </div>

            {/* Time */}
            <p className="text-sm text-gray-600 font-poppins">
              {" "}
              {formData.time || "10:00"} PM
            </p>

            {/* Location */}
            <p className="text-sm text-gray-600 font-poppins">
              Main Cafeteria, Gayatri&nbsp;Hostel
            </p>

            {/* Footer row */}
            <div className="flex items-center justify-between">
              {/* Avatars + count */}
              <div className="flex items-center gap-2">
                <Avatar.Group size="small" maxCount={3}>
                  <Avatar src="/placeholder.svg?height=32&width=32" />
                  <Avatar src="/placeholder.svg?height=32&width=32" />
                  <Avatar src="/placeholder.svg?height=32&width=32" />
                </Avatar.Group>
                <span className="text-sm text-gray-600 font-poppins">
                  50+ going
                </span>
              </div>

              {/* Join button */}
              <Button
                type="primary"
                className="rounded-xl btn-gradient px-6 font-poppins"
              >
                Join&nbsp;Now
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 p-3 rounded-lg mt-2">
          <div className="text-sm font-medium mb-1">Preview on App</div>
          <div className="text-xs text-gray-600">
            This is how your event will appear to students on the Phyber
            mobile app. Review the title, date, time, venue, and visuals to
            ensure everything is accurate and engaging.
          </div>
        </div>
      </div>
    </div>
  </div>
);

const CollapsibleSection = ({ title, isExpanded, onToggle, children }) => (
  <div className="border-b border-gray-200 pb-4 mb-4 w-full ">
    <button
      type="button"
      onClick={onToggle}
      className="flex text-[14px] font-poppins text-[#334155]  justify-between w-full mb-4 hover:text-gray-900 bg-transparent p-[0px]"
    >
      {title}
      {isExpanded ? (
        <ChevronUp className="w-4 h-4" />
      ) : (
        <ChevronDown className="w-4 h-4" />
      )}
    </button>
    {isExpanded && children}
  </div>
);

const ErrorMessage = ({ errors }) => {
  if (!errors || errors.length === 0) return null;

  return (
    <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
      <div className="flex items-start space-x-2">
        <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          {errors.map((error, index) => (
            <p key={index} className="text-sm text-red-700">
              {error}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
};

const SuccessMessage = ({ file }) => {
  if (!file) return null;

  return (
    <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-md">
      <div className="flex items-center space-x-2">
        <CheckCircle className="w-4 h-4 text-green-500" />
        <p className="text-sm text-green-700">
          File uploaded successfully: {file.name} ({formatFileSize(file.size)}
          )
        </p>
      </div>
    </div>
  );
};

const FileUploadArea = ({
  label,
  type,
  file,
  inputRef,
  maxSize,
  fileErrors,
  onFileUpload,
  onRemoveFile,
}) => {
  const hasError = fileErrors[type] && fileErrors[type].length > 0;
  const hasFile = file && !hasError;

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        <span className="text-xs text-gray-500 ml-1">
          (max {formatFileSize(maxSize)})
        </span>
      </label>
      <input
        type="file"
        ref={inputRef}
        onChange={(e) => onFileUpload(type, e)}
        className="hidden"
        accept={type === "poster" ? "image/*" : ".pdf,.doc,.docx"}
      />
      <div
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-md p-4 text-center cursor-pointer transition-colors ${
          hasError
            ? "border-red-300 bg-red-50 hover:border-red-400"
            : hasFile
            ? "border-green-300 bg-green-50 hover:border-green-400"
            : "border-gray-300 hover:border-gray-400"
        }`}
      >
        <Upload
          className={`w-6 h-6 mx-auto mb-2 ${
            hasError
              ? "text-red-400"
              : hasFile
              ? "text-green-400"
              : "text-gray-400"
          }`}
        />
        <button
          type="button"
          className={`bg-transparent font-poppins text-[#171717] ${
            hasError
              ? "text-red-600 hover:text-red-800"
              : hasFile
              ? "text-green-600 hover:text-green-800"
              : "text-blue-600 hover:text-blue-800"
          }`}
        >
          {hasFile ? "Replace file" : "Attach file"}
        </button>
        <p className="text-xs text-gray-500 mt-1">
          {type === "poster" ? "JPEG, PNG, GIF, WebP" : "PDF, DOC, DOCX"}
        </p>
      </div>

      {/* Error Messages */}
      <ErrorMessage errors={fileErrors[type]} />

      {/* Success Message */}
      <SuccessMessage file={hasFile ? file : null} />

      {/* File Info */}
      {hasFile && (
        <div className="flex items-center justify-between mt-2 p-2 bg-gray-50 rounded border">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {file.name}
            </p>
            <p className="text-xs text-gray-500">
              {formatFileSize(file.size)}
            </p>
          </div>
          <div className="flex space-x-2 ml-2">
            <button
              type="button"
              className="text-blue-600 text-sm hover:text-blue-800 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                // In a real app, this would open a file preview
                alert(`Preview: ${file.name}`);
              }}
            >
              View
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemoveFile(type);
              }}
              className="text-red-600 hover:text-red-800 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default function EventForm() {
  const [showPreview, setShowPreview] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    eventInfo: true,
    logistics: true,
    rolePreference: true,
    media: true,
  });
  const [formData, setFormData] = useState({
    eventTitle: "",
    description: "",
    eventFormat: "Online",
    category: "Mental Health",
    repeatOption: "One-time",
    attendeeConfirmation: "RSVP",
    date: "",
    time: "",
    venue: "",
    joiningLink: "",
    maxParticipants: "",
    targetGroup: "",
    rolePreference: "Mentor",
    poster: null,
    agenda: null,
    confirmation: false,
  });
  const [fileErrors, setFileErrors] = useState({
    poster: null,
    agenda: null,
  });

  const posterInputRef = useRef(null);
  const agendaInputRef = useRef(null);

  const handleInputChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const handleFileUpload = (type, event) => {
    const file = event.target.files[0];
    console.log(file);
    if (type === "poster") {
      const imageUrl = URL.createObjectURL(file);
      console.log(imageUrl, type);
      setSelectedImage(imageUrl);
    }

    // Clear previous errors
    setFileErrors((prev) => ({ ...prev, [type]: null }));

    if (file) {
      const validationErrors = validateFile(file, type);

      if (validationErrors.length > 0) {
        setFileErrors((prev) => ({ ...prev, [type]: validationErrors }));
        // Clear the input
        if (event.target) {
          event.target.value = "";
        }
        return;
      }

      // File is valid, update form data
      handleInputChange(type, {
        name: file.name,
        size: file.size,
        type: file.type,
        file: file, // Store the actual file object for potential upload
      });
    }
  };

  const removeFile = (type) => {
    handleInputChange(type, null);
    setFileErrors((prev) => ({ ...prev, [type]: null }));
    if (type === "poster" && posterInputRef.current) {
      posterInputRef.current.value = "";
    }
    if (type === "agenda" && agendaInputRef.current) {
      agendaInputRef.current.value = "";
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Check if there are any file errors
    const hasFileErrors = Object.values(fileErrors).some(
      (error) => error && error.length > 0
    );

    if (hasFileErrors) {
      alert("Please fix file upload errors before submitting the form.");
      return;
    }

    console.log("Form submitted:", formData);
    alert("Event created successfully!");
  };

  return (
    <div className="min-h-screen bg-[#FFFFFF]">
      <div className="max-w-7xl  p-3 ">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-2 space-y-2 sm:space-y-0 ">
          <div className="items-center space-x-2">
            <button
              className="p-2 hover:bg-gray-100 rounded transition-colors bg-white"
              onClick={() => {
                window.history.back();
              }}
            >
              <ArrowLeft className="w-5 h-5" color="#000000" />
            </button>
            <div className="flex items-center space-x-2">
              <div>
                <h1 className="text-[24px] font-poppins text-[#171717] font-bold mb-0">
                  Create New Event (Managed by Admin)
                </h1>
                <p className="text-[#171717] text-[16px] font-poppins ">
                  Organize an event or wellness session for participants.
                </p>
              </div>
            </div>
          </div>
          <div className="flex  space-x-4   h-full items-end justify-end">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showPreview}
                onChange={(e) => setShowPreview(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span>Preview</span>
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Form Section */}
          <div
            className={`${showPreview ? "lg:col-span-7" : "lg:col-span-12"}`}
          >
            <div className="bg-white rounded-lg p-2 shadow-sm">
              <form onSubmit={handleSubmit}>
                {/* Event Information */}
                <CollapsibleSection
                  title="Event Information"
                  isExpanded={expandedSections.eventInfo}
                  onToggle={() => toggleSection("eventInfo")}
                  className="bg-red-500"
                >
                  <div className="space-y-4">
                    <div>
                      <label className="block  text-[#171717] font-poppins mb-2 text-[16px]">
                        Event Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Type here"
                        value={formData.eventTitle}
                        onChange={(e) =>
                          handleInputChange("eventTitle", e.target.value)
                        }
                        className="w-1/2 h-[56px] bg-[#F8FAFC] border-1 border-[#CBD5E1]  px-3 py-2 rounded-[12px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        required
                      />
                    </div>
                    <div>
                      <label className="block  text-[#171717] font-poppins mb-2 text-[16px]">
                        Description
                      </label>
                      <textarea
                        placeholder="Type here"
                        rows={4}
                        value={formData.description}
                        onChange={(e) =>
                          handleInputChange("description", e.target.value)
                        }
                        className="w-full bg-[#F8FAFC] border-1 border-[#CBD5E1] px-3 py-2 rounded-[12px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-vertical"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block  text-[#171717] font-poppins mb-2 text-[16px]">
                          Event Format
                        </label>
                        <select
                          value={formData.eventFormat}
                          onChange={(e) =>
                            handleInputChange("eventFormat", e.target.value)
                          }
                          className="w-full px-3 py-2 h-[56px] bg-[#F8FAFC] border-1 border-[#CBD5E1] rounded-[12px] pr-[12px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        >
                          <option value="Online">Online</option>
                          <option value="In-person">In-person</option>
                          <option value="Hybrid">Hybrid</option>
                        </select>
                      </div>
                      <div>
                        <label className="block  text-[#171717] font-poppins mb-2 text-[16px]">
                          Tags/Category
                        </label>
                        <input
                          type="text"
                          placeholder="Mental Health"
                          value={formData.category}
                          onChange={(e) =>
                            handleInputChange("category", e.target.value)
                          }
                          className="w-full px-3 py-2 h-[56px] bg-[#F8FAFC] border-1 border-[#CBD5E1] rounded-[12px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block  text-[#171717] font-poppins mb-2 text-[16px]">
                          Event Repeat Option
                        </label>
                        <select
                          value={formData.repeatOption}
                          onChange={(e) =>
                            handleInputChange("repeatOption", e.target.value)
                          }
                          className="w-full px-3 py-2 h-[56px] bg-[#F8FAFC] border-1 border-[#CBD5E1] rounded-[12px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        >
                          <option value="One-time">One-time</option>
                          <option value="Daily">Daily</option>
                          <option value="Weekly">Weekly</option>
                          <option value="Monthly">Monthly</option>
                        </select>
                      </div>
                      <div>
                        <label className="block  text-[#171717] font-poppins mb-2 text-[16px]">
                          Attendee Confirmation
                        </label>
                        <input
                          type="text"
                          placeholder="RSVP"
                          value={formData.attendeeConfirmation}
                          onChange={(e) =>
                            handleInputChange(
                              "attendeeConfirmation",
                              e.target.value
                            )
                          }
                          className="w-full px-3 py-2 h-[56px] bg-[#F8FAFC] border-1 border-[#CBD5E1] rounded-[12px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        />
                      </div>
                    </div>
                  </div>
                </CollapsibleSection>

                {/* Event Logistics */}
                <CollapsibleSection
                  title="Event Logistics"
                  isExpanded={expandedSections.logistics}
                  onToggle={() => toggleSection("logistics")}
                >
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block  text-[#171717] font-poppins mb-2 text-[16px]">
                          Date
                        </label>
                        <input
                          type="date"
                          value={formData.date}
                          onChange={(e) =>
                            handleInputChange("date", e.target.value)
                          }
                          className="w-full px-3 py-2 h-[56px] bg-[#F8FAFC] border-1 border-[#CBD5E1] rounded-[12px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block  text-[#171717] font-poppins mb-2 text-[16px]">
                          Time
                        </label>
                        <input
                          type="time"
                          value={formData.time}
                          onChange={(e) =>
                            handleInputChange("time", e.target.value)
                          }
                          className="w-full px-3 py-2 h-[56px] bg-[#F8FAFC] border-1 border-[#CBD5E1] rounded-[12px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block  text-[#171717] font-poppins mb-2 text-[16px]">
                          Venue (if in-person)
                        </label>
                        <input
                          type="text"
                          placeholder="Type here"
                          value={formData.venue}
                          onChange={(e) =>
                            handleInputChange("venue", e.target.value)
                          }
                          className="w-full px-3 py-2 h-[56px] bg-[#F8FAFC] border-1 border-[#CBD5E1] rounded-[12px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block  text-[#171717] font-poppins mb-2 text-[16px]">
                          Joining Link (if online)
                        </label>
                        <div className="flex space-x-2">
                          <input
                            type="url"
                            placeholder="Paste here"
                            value={formData.joiningLink}
                            onChange={(e) =>
                              handleInputChange("joiningLink", e.target.value)
                            }
                            className="flex-1 px-3 py-2 h-[56px] bg-[#F8FAFC] border-1 border-[#CBD5E1] rounded-[12px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block  text-[#171717] font-poppins mb-2 text-[16px]">
                          Max Participants
                        </label>

                        <input
                          type="number"
                          placeholder="100"
                          value={formData.maxParticipants}
                          onChange={(e) =>
                            handleInputChange("maxParticipants", e.target.value)
                          }
                          className="flex-1 w-full px-3 py-2 h-[56px] bg-[#F8FAFC] border-1 border-[#CBD5E1] rounded-[12px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block  text-[#171717] font-poppins mb-2 text-[16px]">
                          Target Group
                        </label>

                        <input
                          type="text"
                          placeholder="Target Group"
                          value={formData.targetGroup}
                          onChange={(e) =>
                            handleInputChange("targetGroup", e.target.value)
                          }
                          className="flex-1 w-full px-3 py-2 h-[56px] bg-[#F8FAFC] border-1 border-[#CBD5E1] rounded-[12px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        />
                      </div>
                    </div>
                  </div>
                </CollapsibleSection>

                {/* Role Preference */}
                <CollapsibleSection
                  title="Role Preference"
                  isExpanded={expandedSections.rolePreference}
                  onToggle={() => toggleSection("rolePreference")}
                >
                  <div>
                    <label className="block  text-[#171717] font-poppins mb-2 text-[16px]">
                      I want to participate as
                    </label>
                    <div className="flex flex-wrap gap-6">
                      {["Host", "Participent"].map((role) => (
                        <label
                          key={role}
                          className="flex items-center space-x-2 cursor-pointer"
                        >
                          <input
                            type="radio"
                            name="rolePreference"
                            value={role}
                            checked={formData.rolePreference === role}
                            onChange={(e) =>
                              handleInputChange(
                                "rolePreference",
                                e.target.value
                              )
                            }
                            className="text-[#171717] focus:ring-blue-500 w-4 h-4"
                          />
                          <span className="text-[16px] text-[#171717] font-poppins">
                            {role}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </CollapsibleSection>

                {/* Media/Attachments */}
                <CollapsibleSection
                  title="Media / Attachments"
                  isExpanded={expandedSections.media}
                  onToggle={() => toggleSection("media")}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FileUploadArea
                      label={
                        <span className="text-[14px] font-poopins text-[#171717]">
                          Upload Poster
                        </span>
                      }
                      type="poster"
                      file={formData.poster}
                      inputRef={posterInputRef}
                      maxSize={FILE_SIZE_LIMITS.poster}
                      fileErrors={fileErrors}
                      onFileUpload={handleFileUpload}
                      onRemoveFile={removeFile}
                    />
                    <FileUploadArea
                      label={
                        <span className="text-[14px] font-poopins text-[#171717]">
                          Upload Agenda
                        </span>
                      }
                      type="agenda"
                      file={formData.agenda}
                      inputRef={agendaInputRef}
                      maxSize={FILE_SIZE_LIMITS.agenda}
                      fileErrors={fileErrors}
                      onFileUpload={handleFileUpload}
                      onRemoveFile={removeFile}
                    />
                  </div>
                </CollapsibleSection>

                {/* Confirmation */}
                <div className="mt-6">
                  <label className="flex items-start space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.confirmation}
                      onChange={(e) =>
                        handleInputChange("confirmation", e.target.checked)
                      }
                      className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-[14px] font-poppins text-[#334155] ">
                      I confirm that the information provided above is accurate,
                      and I agree to follow Phyber's event guidelines and
                      community standards.
                    </span>
                  </label>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-4 mt-6">
                  <button
                    type="button"
                    className="w-full sm:w-auto px-6 py-2 border-1 border-[#4B61FF] text-[#4B61FF] font-poppins rounded-[8px] bg-[#FFFFFF]"
                    style={{ border: "1px solid #4B61FF" }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled
                    className="w-full sm:w-auto px-6 py-2 bg-[#C7C7C7] font-poppins text-[#FFFFFF] "
                  >
                    Create Event
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Preview Section */}
          {showPreview && (
            <div className="lg:col-span-5">
              <div className="sticky top-4">
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <div className="flex items-center space-x-2 mb-4">
                    <span className="font-[18px] font-poppins text-[#171717]">
                      Preview on App
                    </span>
                    <Info className="w-4 h-4 text-gray-400" />
                  </div>
                  <MobilePreview formData={formData} selectedImage={selectedImage} />
                  <div className="mt-4">
                    <p className="text-[#334155] font-poppins text-[14px]">
                      This is how your event will appear to students on the
                      Phyber mobile app. Review the title, date, time, venue,
                      and visuals to ensure everything is accurate and engaging.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
